import { Button } from '@/components/shadcn/button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { ErrorComponent } from '@/ui-legacy/ErrorComponent'
import { Loading } from '@/ui-legacy/Loading'
import { WebAsset20Regular } from '@fluentui/react-icons'
import { IconDeleteOutline } from '@icons/IconDeleteOutline'
import { IconEditOutline } from '@icons/IconEditOutline'
import { IconPlusOutline } from '@icons/IconPlusOutline'
import { useCallback } from 'react'
import { toast } from 'sonner'
import { FileTemplateFormV2 } from '../../Forms/FileTemplateFormV2'

type MessagesEntityTypesExistingEntityType = RestSchemas['Messages.EntityTypes.ExistingEntityType']
type MessagesTemplatesFileTemplate = RestSchemas['Messages.Templates.FileTemplate']

export function FileTemplatesList({
  entityType,
  applicationId,
}: {
  entityType: MessagesEntityTypesExistingEntityType
  applicationId: number
}) {
  const { getTemplatesApiListFileTemplates } = useRestClient()

  const {
    data: fileTemplates,
    isPending,
    isError,
  } = getTemplatesApiListFileTemplates.useQuery({
    query: { applicationId: applicationId, entityTypeId: entityType.id },
  })

  const invalidateTemplates = useCallback(
    () =>
      getTemplatesApiListFileTemplates.invalidateQueries({
        parameters: { query: { applicationId: applicationId, entityTypeId: entityType.id } },
      }),
    [entityType.id, getTemplatesApiListFileTemplates, applicationId]
  )

  if (isPending) return <Loading />
  if (isError) return <ErrorComponent />

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row items-center justify-between">
        <h6 className="text-sm">File Templates:</h6>
        <FileTemplateFormV2
          sheetTitle="Add File Template"
          sheetDescription="Add a new file template to your entity type"
          sheetTriggerTooltip="Add file template"
          onSubmit={invalidateTemplates}
          selectedEntityType={entityType}
          sheetTrigger={
            <Button variant="outline" size="icon" className="size-6 rounded-full">
              <IconPlusOutline />
              <span className="sr-only">Add File Template</span>
            </Button>
          }
        />
      </div>
      <ul className="w-full divide-y divide-gray-100 font-light">
        {fileTemplates.length > 0 ? (
          fileTemplates.map((ft) => (
            <ListItem key={ft.id} fileTemplate={ft} entityType={entityType} invalidateTemplates={invalidateTemplates} />
          ))
        ) : (
          <div className="italic">...No file templates found...</div>
        )}
      </ul>
    </div>
  )
}

function ListItem({
  fileTemplate,
  entityType,
  invalidateTemplates,
}: {
  fileTemplate: MessagesTemplatesFileTemplate
  entityType: MessagesEntityTypesExistingEntityType
  invalidateTemplates: () => void
}) {
  const { deleteTemplatesApiDeleteTemplate } = useRestClient()

  const { mutate: deleteTemplate } = deleteTemplatesApiDeleteTemplate.useMutation()

  const handleDelete = () => {
    deleteTemplate(
      { body: fileTemplate },
      {
        onSuccess: () => {
          toast.success('Template successfully deleted')
          invalidateTemplates()
        },
        onError: () => toast.error('Error deleting template'),
      }
    )
  }

  return (
    <>
      <li key={fileTemplate.id} className="flex items-center justify-between">
        <div className="flex shrink-0 items-center gap-2">
          <WebAsset20Regular />
          {fileTemplate.name}
        </div>
        <div className="flex items-center">
          <FileTemplateFormV2
            invalidateTemplates={invalidateTemplates}
            selectedTemplate={fileTemplate}
            selectedEntityType={entityType}
            sheetTitle="Edit File Template"
            sheetTriggerTooltip="Edit"
            sheetDescription="Edit the file template"
            sheetTrigger={
              <Button variant="ghost" size="icon" className="h-6 w-6">
                <IconEditOutline size={16} />
                <span className="sr-only">Edit File Template</span>
              </Button>
            }
          />
          <ConfirmDialog
            title="Confirm Delete"
            description="Are you sure you want to delete this file template?"
            onConfirm={handleDelete}
            triggerTooltip="Delete"
            trigger={
              <Button size="icon" variant="ghost" className="h-6 w-6">
                <IconDeleteOutline size={16} />
              </Button>
            }
          />
        </div>
      </li>
    </>
  )
}
